import { Zap, Clock, Globe, Home, FileText, X, Check } from "lucide-react";
import { motion } from "motion/react";

export function WhyGreenTariff() {
  const alternatives = [
    {
      icon: Zap,
      title: "Open Access",
      problems: ["Binding long-term PPAs", "Policy uncertainties", "Complex wheeling charges", "Banking limitations"],
      gradient: "from-amber-400 to-amber-600"
    },
    {
      icon: FileText,
      title: "Green Power Trading",
      problems: ["Process challenges", "Market volatility", "Transaction complexity", "Limited availability"],
      gradient: "from-yellow-400 to-yellow-600"
    },
    {
      icon: Home,
      title: "Rooftop Solar",
      problems: ["Lack of physical space", "High upfront costs", "Maintenance overhead", "Structural constraints"],
      gradient: "from-orange-400 to-orange-600"
    },
  ];

  const greenTariffBenefits = [
    {
      icon: Check,
      title: "No Infrastructure Required",
      description: "Switch to verified renewable energy without any physical installation or space constraints.",
      gradient: "from-emerald-400 to-emerald-600"
    },
    {
      icon: Check,
      title: "No Long-Term Commitments",
      description: "Flexible tariff structure with no binding PPAs or policy uncertainties. Switch anytime with ease.",
      gradient: "from-cyan-400 to-cyan-600"
    },
    {
      icon: Check,
      title: "Hassle-Free Process",
      description: "Simple onboarding in minutes. No complex trading, wheeling charges, or banking complications.",
      gradient: "from-emerald-400 to-cyan-600"
    }
  ];

  return (
    <section id="why-green" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl mb-4 bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
            Why Green Tariff?
          </h2>

        </motion.div>

        {/* Challenges with Alternatives */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-2xl text-center mb-8 text-gray-800">
            Challenges with Traditional Green Energy Options
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            {alternatives.map((alt, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="h-full p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-200 hover:border-gray-300 transition-all">
                  <div className={`w-14 h-14 bg-gradient-to-br ${alt.gradient} rounded-xl flex items-center justify-center mb-4`}>
                    <alt.icon className="w-7 h-7 text-white" />
                  </div>
                  
                  <h4 className="text-lg mb-4 text-gray-900">
                    {alt.title}
                  </h4>
                  
                  <ul className="space-y-2">
                    {alt.problems.map((problem, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        <span>{problem}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Green Tariff Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h3 className="text-2xl text-center mb-8 text-gray-800">
            Green Tariff Solution
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            {greenTariffBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                <div className="h-full p-8 bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 transition-all hover:shadow-xl">
                  <div className={`w-16 h-16 bg-gradient-to-br ${benefit.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                    <benefit.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="text-xl mb-3 text-gray-900">
                    {benefit.title}
                  </h3>
                  
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Additional Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 text-center"
        >
          <div className="inline-block bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-2xl p-8 text-white shadow-xl">
            <div className="flex flex-wrap justify-center items-center gap-6 md:gap-12">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8" />
                <div className="text-left">
                  <div className="text-2xl">24/7</div>
                  <div className="text-sm opacity-90">Hourly Matching</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Globe className="w-8 h-8" />
                <div className="text-left">
                  <div className="text-2xl">RE100</div>
                  <div className="text-sm opacity-90">Compatible</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Zap className="w-8 h-8" />
                <div className="text-left">
                  <div className="text-2xl">100%</div>
                  <div className="text-sm opacity-90">Renewable</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
